
import * as React from "react";
import { format, addDays } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DateRangePickerProps {
  className?: string;
  from: Date | null;
  to: Date | null;
  onFromChange: (date: Date | null) => void;
  onToChange: (date: Date | null) => void;
  maxDateRange?: number; // Optional max date range in days
}

export function DateRangePicker({
  className,
  from,
  to,
  onFromChange,
  onToChange,
  maxDateRange,
}: DateRangePickerProps) {
  
  // Format the date for display or show placeholder
  const formatDate = (date: Date | null): string => {
    return date ? format(date, "MMM dd, yyyy") : "Select date";
  };

  // When start date changes, adjust end date if needed
  const handleFromChange = (date: Date | null) => {
    onFromChange(date);
    
    // If maxDateRange is specified and we have a valid start date
    if (maxDateRange && date) {
      // If end date is not set or would exceed max range, adjust it
      if (!to || (to && (addDays(date, maxDateRange) < to))) {
        const newEndDate = addDays(date, maxDateRange);
        onToChange(newEndDate);
      }
    }
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Start Date Selector */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[140px] justify-start text-left font-normal",
              !from && "text-muted-foreground",
              "bg-white border-gray-300 text-gray-800 hover:border-primary"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {from ? format(from, "MMM dd") : "Start"}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0" 
          align="start"
          sideOffset={4}
          style={{ zIndex: 999999, pointerEvents: 'auto' }}
        >
          <Calendar
            initialFocus
            mode="single"
            selected={from || undefined}
            onSelect={(date) => handleFromChange(date || null)}
            disabled={(date) => to ? date > to : false}
            style={{ zIndex: 999999, pointerEvents: 'auto' }}
          />
        </PopoverContent>
      </Popover>

      <span className="text-gray-500">-</span>

      {/* End Date Selector */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline" 
            className={cn(
              "w-[140px] justify-start text-left font-normal",
              !to && "text-muted-foreground",
              "bg-white border-gray-300 text-gray-800 hover:border-primary"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {to ? format(to, "MMM dd") : "End"}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0"
          align="start"
          sideOffset={4}
          style={{ zIndex: 999999, pointerEvents: 'auto' }}
        >
          <Calendar
            initialFocus
            mode="single"
            selected={to || undefined}
            onSelect={(date) => onToChange(date || null)}
            disabled={(date) => {
              // Disable dates before the start date
              if (from && date < from) return true;
              
              // If maxDateRange is specified, also disable dates beyond the max range
              if (maxDateRange && from && date > addDays(from, maxDateRange)) {
                return true;
              }
              
              return false;
            }}
            style={{ zIndex: 999999, pointerEvents: 'auto' }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
